import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { politicaParceiroService } from '@/services/produtoService'
import { CriterioParceiro, PoliticaParceiro } from '@/types/produto'

/**
 * Política de elegibilidade de um parceiro para um produto
 * (PDF "Cadastro de Elegibilidade do Parceiro").
 *
 * **A granularidade vem do documento**: *"POLÍTICA DO PRODUTO (Repete para cada produto que
 * o parceiro opera)"*. Por isso o diálogo abre a partir do produto — o par (parceiro,
 * produto) já está determinado pelo contexto, e não há como preencher a política errada.
 *
 * **O formulário é dado**: os campos vêm do catálogo semeado do PDF, agrupados nas mesmas
 * seções do documento. A seção que o PDF diz que *"muda conforme o produto"* chega
 * resolvida pelo backend — a tela não sabe quais critérios são de qual produto, e não
 * precisa saber.
 */

/** Sim/Não/(vazio): o vazio importa — é "o parceiro não informou", não "não aceita". */
const BOOLEANOS = [
  { valor: '', rotulo: '— não informado —' },
  { valor: 'true', rotulo: 'Sim' },
  { valor: 'false', rotulo: 'Não' },
]

interface Props {
  aberto: boolean
  parceiroId: string
  parceiroNome: string
  produtoId: string
  podeEditar: boolean
  onFechar: () => void
}

const PoliticaParceiroDialog: React.FC<Props> = ({
  aberto,
  parceiroId,
  parceiroNome,
  produtoId,
  podeEditar,
  onFechar,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [politica, setPolitica] = useState<PoliticaParceiro | null>(null)
  const [valores, setValores] = useState<Record<string, string>>({})
  const [eliminatorios, setEliminatorios] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [ativa, setAtiva] = useState(true)
  const [carregando, setCarregando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const p = await politicaParceiroService.obter(parceiroId, produtoId)
      setPolitica(p)
      setValores(
        Object.fromEntries(
          p.criterios.filter((c) => c.valor != null).map((c) => [c.definicaoId, c.valor as string])
        )
      )
      setEliminatorios(p.criteriosEliminatorios ?? '')
      setObservacoes(p.observacoesComerciais ?? '')
      setAtiva(p.ativa)
    } catch {
      setErro('Não foi possível carregar a política deste parceiro.')
    } finally {
      setCarregando(false)
    }
  }, [parceiroId, produtoId])

  useEffect(() => {
    if (aberto) void carregar()
  }, [aberto, carregar])

  const salvar = async () => {
    setSalvando(true)
    try {
      await politicaParceiroService.salvar(parceiroId, produtoId, {
        valores,
        criteriosEliminatorios: eliminatorios.trim() || null,
        observacoesComerciais: observacoes.trim() || null,
        ativa,
      })
      enqueueSnackbar('Política salva.', { variant: 'success' })
      onFechar()
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao salvar a política.', {
        variant: 'error',
      })
    } finally {
      setSalvando(false)
    }
  }

  /** Agrupa na ordem em que o backend devolveu, que é a ordem das seções do PDF. */
  const secoes = useMemo(() => {
    const mapa = new Map<string, CriterioParceiro[]>()
    politica?.criterios.forEach((c) => {
      if (!mapa.has(c.secao)) mapa.set(c.secao, [])
      mapa.get(c.secao)?.push(c)
    })
    return Array.from(mapa.entries())
  }, [politica])

  const preenchidos = Object.values(valores).filter((v) => v && v.trim() !== '').length
  const total = politica?.total ?? 0

  const campo = (c: CriterioParceiro) => {
    const valor = valores[c.definicaoId] ?? ''
    const alterar = (v: string) =>
      setValores((prev) => ({ ...prev, [c.definicaoId]: v }))

    if (c.tipoValor === 'BOOLEAN') {
      return (
        <TextField
          key={c.definicaoId}
          label={c.rotulo}
          select
          value={valor}
          onChange={(e) => alterar(e.target.value)}
          disabled={!podeEditar}
          size="small"
          fullWidth
        >
          {BOOLEANOS.map((b) => (
            <MenuItem key={b.valor} value={b.valor}>
              {b.rotulo}
            </MenuItem>
          ))}
        </TextField>
      )
    }
    if (c.tipoValor === 'NUMERO') {
      return (
        <TextField
          key={c.definicaoId}
          label={c.rotulo}
          type="number"
          value={valor}
          onChange={(e) => alterar(e.target.value)}
          disabled={!podeEditar}
          size="small"
          fullWidth
        />
      )
    }
    return (
      <TextField
        key={c.definicaoId}
        label={c.rotulo}
        value={valor}
        onChange={(e) => alterar(e.target.value)}
        disabled={!podeEditar}
        multiline
        minRows={2}
        helperText={c.tipoValor === 'LISTA' ? 'Um item por linha.' : undefined}
        size="small"
        fullWidth
      />
    )
  }

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle>
        Política de elegibilidade — {parceiroNome}
        {politica?.produtoNome && (
          <Typography variant="body2" color="textSecondary">
            Produto: {politica.produtoNome}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {carregando ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress aria-label="Carregando política" />
          </Box>
        ) : erro ? (
          <Alert severity="error">{erro}</Alert>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="textSecondary">
                  {preenchidos} de {total} critérios preenchidos
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ativa}
                      onChange={(e) => setAtiva(e.target.checked)}
                      disabled={!podeEditar}
                    />
                  }
                  label={ativa ? 'Política ativa' : 'Política inativa'}
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={total === 0 ? 0 : (preenchidos / total) * 100}
                sx={{ mt: 0.5 }}
              />
            </Box>

            {secoes.map(([secao, criterios]) => (
              <Box key={secao}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {secao}
                  </Typography>
                  {criterios[0]?.produtoCategoria && (
                    <Chip size="small" label="Específico deste produto" variant="outlined" />
                  )}
                </Stack>
                <Stack spacing={2}>{criterios.map(campo)}</Stack>
              </Box>
            ))}

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Critérios eliminatórios
              </Typography>
              <TextField
                value={eliminatorios}
                onChange={(e) => setEliminatorios(e.target.value)}
                placeholder="Ex.: não aceita operação abaixo de R$ 500 mil; não aceita imóvel rural."
                helperText="Tudo o que faz o parceiro rejeitar automaticamente uma operação."
                disabled={!podeEditar}
                multiline
                minRows={3}
                fullWidth
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Observações comerciais
              </Typography>
              <TextField
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Ex.: está priorizando Home Equity acima de R$ 2 milhões; prazo médio de análise de 48 horas."
                disabled={!podeEditar}
                multiline
                minRows={2}
                fullWidth
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Fechar</Button>
        {podeEditar && (
          <Button variant="contained" onClick={() => void salvar()} disabled={salvando || carregando}>
            {salvando ? 'Salvando…' : 'Salvar política'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default PoliticaParceiroDialog
